/**
 * Shopify Products Hooks
 * 
 * React Query hooks for fetching Shopify products and collections with support for:
 * - Regular queries with caching
 * - Infinite queries for pagination/infinite scroll
 * - Error handling and loading states
 * - Automatic refetching and background updates
 * - Optimized GraphQL queries to reduce over-fetching
 * - Advanced request deduplication
 * - Intelligent caching strategies per data type
 */

import { useQuery, useInfiniteQuery, UseQueryResult, UseInfiniteQueryResult } from '@tanstack/react-query';
import { shopifyApi } from '../services/shopifyApi';
import { ShopifyProduct, ShopifyCollection, ShopifyEdge } from '../types/shopify';
import { queryKeyUtils } from '../lib/enhancedQueryClient';
import requestDeduplicationService from '../services/requestDeduplicationService';
import { useProductCaching, DataType } from './useCachingStrategy';

// Query options for products
export interface ProductQueryOptions {
  first?: number;
  after?: string;
  query?: string;
  sortKey?: 'TITLE' | 'PRICE' | 'CREATED_AT' | 'UPDATED_AT' | 'BEST_SELLING' | 'RELEVANCE';
  reverse?: boolean;
  useOptimized?: boolean; // Flag to use optimized queries
  useCase?: string; // For deduplication optimization
  dataType?: DataType; // For caching strategy
}

// Query options for collections
export interface CollectionQueryOptions {
  first?: number;
  after?: string;
  query?: string;
  sortKey?: 'TITLE' | 'UPDATED_AT' | 'ID' | 'RELEVANCE';
  reverse?: boolean;
  useOptimized?: boolean;
  useCase?: string;
  dataType?: DataType;
}

/**
 * Hook for fetching products with regular query (optimized by default)
 */
export const useShopifyProducts = (
  options: ProductQueryOptions = {}
): UseQueryResult<ShopifyProduct[], Error> => {
  const { 
    useOptimized = true, 
    useCase = 'product-listing',
    dataType = DataType.PRODUCT_CATALOG,
    ...queryOptions 
  } = options;
  
  // Get appropriate caching strategy
  const { catalogStrategy } = useProductCaching();
  
  // Generate normalized query key for better deduplication
  const queryKey = queryKeyUtils.products({
    first: queryOptions.first,
    after: queryOptions.after,
    sortKey: queryOptions.sortKey,
    reverse: queryOptions.reverse,
    query: queryOptions.query,
    optimized: useOptimized
  });
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      const startTime = Date.now();
      
      try {
        let response;
        
        if (queryOptions.query) {
          // Use search query for filtered results
          response = useOptimized 
            ? await shopifyApi.searchProducts(
                queryOptions.query,
                queryOptions.first,
                queryOptions.after,
                queryOptions.sortKey,
                queryOptions.reverse
              )
            : await shopifyApi.getProducts(queryOptions.first, queryOptions.after);
        } else {
          // Use listing query for general product display
          response = useOptimized
            ? await shopifyApi.getProductsOptimized(
                queryOptions.first,
                queryOptions.after,
                queryOptions.sortKey,
                queryOptions.reverse
              )
            : await shopifyApi.getProducts(queryOptions.first, queryOptions.after);
        }
        
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheHit(responseTime);
        
        return response.products.edges.map((edge: ShopifyEdge<ShopifyProduct>) => edge.node);
      } catch (error) {
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheMiss(responseTime);
        throw error;
      }
    },
    // Apply intelligent caching strategy
    ...catalogStrategy,
    meta: {
      useCase,
      optimized: useOptimized,
      dataType
    }
  });
};

/**
 * Hook for fetching products with infinite query (pagination/infinite scroll)
 */
type ProductsPage = {
  products: ShopifyProduct[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
};

const isProductsPage = (value: unknown): value is ProductsPage => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const page = value as {
    products?: unknown;
    pageInfo?: unknown;
  };

  const hasProductsArray = Array.isArray(page.products);
  const hasPageInfo =
    typeof page.pageInfo === 'object' &&
    page.pageInfo !== null &&
    'hasNextPage' in (page.pageInfo as Record<string, unknown>);

  return hasProductsArray && hasPageInfo;
};

export const useInfiniteShopifyProducts = (
  options: Omit<ProductQueryOptions, 'after'> = {}
) => {
  const { 
    useOptimized = true, 
    useCase = 'infinite-scroll',
    dataType = DataType.PRODUCT_CATALOG,
    ...queryOptions 
  } = options;
  
  // Get appropriate caching strategy
  const { catalogStrategy } = useProductCaching();
  
  // Generate normalized query key for better deduplication
  const queryKey = queryKeyUtils.infiniteProducts({
    first: queryOptions.first,
    sortKey: queryOptions.sortKey,
    reverse: queryOptions.reverse,
    query: queryOptions.query,
    optimized: useOptimized
  });
  
  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const startTime = Date.now();
      
      try {
        let response;
        
        if (queryOptions.query) {
          response = useOptimized
            ? await shopifyApi.searchProducts(
                queryOptions.query,
                queryOptions.first,
                pageParam as string | undefined,
                queryOptions.sortKey,
                queryOptions.reverse
              )
            : await shopifyApi.getProducts(queryOptions.first, pageParam as string | undefined);
        } else {
          response = useOptimized
            ? await shopifyApi.getProductsOptimized(
                queryOptions.first,
                pageParam as string | undefined,
                queryOptions.sortKey,
                queryOptions.reverse
              )
            : await shopifyApi.getProducts(queryOptions.first, pageParam as string | undefined);
        }
        
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheHit(responseTime);
        
        return {
          products: response.products.edges.map((edge: ShopifyEdge<ShopifyProduct>) => edge.node),
          pageInfo: response.products.pageInfo
        };
      } catch (error) {
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheMiss(responseTime);
        throw error;
      }
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      return firstPage.pageInfo.hasPreviousPage ? firstPage.pageInfo.startCursor : undefined;
    },
    // Apply intelligent caching strategy
    ...catalogStrategy,
    meta: {
      useCase,
      optimized: useOptimized,
      dataType
    }
  });
};

/**
 * Hook for fetching collections with regular query
 */
export const useShopifyCollections = (
  options: CollectionQueryOptions = {}
): UseQueryResult<ShopifyCollection[], Error> => {
  const { 
    useOptimized = true, 
    useCase = 'collections',
    dataType = DataType.COLLECTIONS,
    ...queryOptions 
  } = options;
  
  // Get appropriate caching strategy
  const { collectionsStrategy } = useProductCaching();
  
  // Generate normalized query key for better deduplication
  const queryKey = queryKeyUtils.collections({
    first: queryOptions.first,
    after: queryOptions.after,
    sortKey: queryOptions.sortKey,
    reverse: queryOptions.reverse,
    optimized: useOptimized
  });
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      const startTime = Date.now();
      
      try {
        const response = useOptimized
          ? await shopifyApi.getCollectionsOptimized(queryOptions.first, queryOptions.after)
          : await shopifyApi.getCollections(queryOptions.first, queryOptions.after);
        
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheHit(responseTime);
        
        return response.collections.edges.map((edge: ShopifyEdge<ShopifyCollection>) => edge.node);
      } catch (error) {
        const responseTime = Date.now() - startTime;
        requestDeduplicationService.recordCacheMiss(responseTime);
        throw error;
      }
    },
    // Apply intelligent caching strategy
    ...collectionsStrategy,
    meta: {
      useCase,
      optimized: useOptimized,
      dataType
    }
  });
};

/**
 * Hook for fetching collections with infinite query
 */
export const useInfiniteShopifyCollections = (
  options: Omit<CollectionQueryOptions, 'after'> = {}
): UseInfiniteQueryResult<{
  collections: ShopifyCollection[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
}, Error> => {
  return useInfiniteQuery({
    queryKey: ['shopify-collections-infinite', options],
    queryFn: async ({ pageParam }) => {
      const response = await shopifyApi.getCollections(
        options.first,
        pageParam as string | undefined
      );
      
      return {
        collections: response.collections.edges.map((edge: ShopifyEdge<ShopifyCollection>) => edge.node),
        pageInfo: response.collections.pageInfo
      };
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      return firstPage.pageInfo.hasPreviousPage ? firstPage.pageInfo.startCursor : undefined;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Utility hook to get all products from infinite query as a flat array
 */
export const useAllInfiniteProducts = (
  options: Omit<ProductQueryOptions, 'after'> = {}
) => {
  const infiniteQuery = useInfiniteShopifyProducts(options);

  const rawPages: unknown[] = infiniteQuery.data?.pages ?? [];
  const pages = rawPages.filter(isProductsPage);
  const allProducts = pages.flatMap((page) => page.products);
  const lastPage = pages[pages.length - 1];

  return {
    products: allProducts,
    totalCount: allProducts.length,
    hasNextPage: lastPage?.pageInfo.hasNextPage ?? false,
    isLoading: infiniteQuery.isLoading,
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
};
